import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useId, useRef, useState } from "react";
import { toast } from "sonner";
import { useDashboardContext } from "@/components/dashboard/DashboardContext";
import {
	DashboardLayout,
} from "@/components/dashboard/DashboardLayout";
import { useSession } from "@/hooks/auth-hooks";

export const Route = createFileRoute("/dashboard/billing")({
	validateSearch: (search: Record<string, unknown>) => ({
		checkout:
			search.checkout === "success" || search.checkout === "cancelled"
				? search.checkout
				: undefined,
	}),
	component: BillingPage,
});

type PresetAmount = 25 | 50 | 100 | 250;

const PRESETS: {
	amount: PresetAmount;
	label?: string;
}[] = [
	{ amount: 25 },
	{ amount: 50, label: "Popular" },
	{ amount: 100 },
	{ amount: 250, label: "Best for Teams" },
];

function formatUSD(amount: number): string {
	return `$${amount.toFixed(2)}`;
}

const billingTheme = {
	accent: "#6B3FF2",
	accentStrong: "#ffffff",
	accentSoft: "#2b9a66",
	success: "#2b9a66",
	muted: "#adaaaa",
	text: "#fcfcfc",
	surface: "#131313",
	surfaceAlt: "#1a1a1a",
	surfaceInset: "#0c0c0c",
	surfaceGlass: "rgba(19,19,19,0.82)",
	border: "rgba(255,255,255,0.08)",
	borderSoft: "rgba(255,255,255,0.06)",
	borderAccent: "rgba(234,40,4,0.4)",
	rowFill: "#181818",
};

type BillingTransaction = {
	amountCents: number;
	createdAt: string;
	description: string | null;
	generationId: string | null;
	id: string;
	provider: string;
	stripeCheckoutSessionId: string | null;
	transactionType: string;
};

function formatMoneyFromCents(amountCents: number) {
	return formatUSD(amountCents / 100);
}

function getTransactionLabel(transaction: BillingTransaction) {
	if (transaction.description) return transaction.description;
	if (transaction.transactionType === "credit") return "Balance top-up";
	return "Usage charge";
}

function getProviderLabel(provider: string) {
	if (provider === "stripe") return "Stripe";
	if (provider === "runware") return "Usage";
	if (provider === "admin") return "Admin";
	return provider;
}

function TransferFundsSection({
	activeWorkspaceId,
	activeBalanceCents,
	onTransferSuccess,
}: {
	activeWorkspaceId: string | null;
	activeBalanceCents: number;
	onTransferSuccess: () => void;
}) {
	const workspacesQuery = useQuery({
		queryKey: ["workspaces"],
		queryFn: async () => {
			const res = await fetch("/api/workspaces", { headers: { Accept: "application/json" } });
			if (!res.ok) throw new Error("Failed to fetch workspaces");
			return res.json() as Promise<{ workspaces: { id: string; name: string; slug: string; role: string; isActive: boolean }[] }>;
		},
	});
	const otherWorkspaces = (workspacesQuery.data?.workspaces ?? []).filter((ws) => ws.id !== activeWorkspaceId);
	const [toWorkspaceId, setToWorkspaceId] = useState("");
	const effectiveToWorkspaceId = toWorkspaceId || otherWorkspaces[0]?.id || "";
	const [amount, setAmount] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	const amountCents = Math.round(parseFloat(amount || "0") * 100);
	const availableDollars = (activeBalanceCents / 100).toFixed(2);

	const handleTransfer = async () => {
		if (amountCents <= 0) {
			toast.error("Enter a valid amount");
			return;
		}
		if (amountCents > activeBalanceCents) {
			toast.error(`Insufficient balance. Available: $${availableDollars}`);
			return;
		}
		if (!effectiveToWorkspaceId || !activeWorkspaceId) return;

		setIsSubmitting(true);
		try {
			const res = await fetch("/api/workspaces/transfer-funds", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					amountCents,
					fromWorkspaceId: activeWorkspaceId,
					toWorkspaceId: effectiveToWorkspaceId,
				}),
			});
			const data = (await res.json()) as { error?: string };
			if (!res.ok) throw new Error(data.error ?? "Transfer failed");
			toast.success(`$${(amountCents / 100).toFixed(2)} transferred successfully`);
			setAmount("");
			onTransferSuccess();
		} catch (error) {
			toast.error(error instanceof Error ? error.message : "Transfer failed");
		} finally {
			setIsSubmitting(false);
		}
	};

	if (otherWorkspaces.length === 0) return null;

	return (
		<div>
			<div className="flex items-center gap-4 mb-6">
				<h2
					className="text-xs uppercase tracking-[0.1em] font-bold shrink-0"
					style={{ fontFamily: "inherit", color: "#6B3FF2" }}
				>
					Transfer Funds
				</h2>
				<div className="h-px flex-1" style={{ backgroundColor: billingTheme.borderSoft }} />
			</div>
			<div
				className="p-6"
				style={{ backgroundColor: billingTheme.surface, border: `1px solid ${billingTheme.border}` }}
			>
				<div className="flex flex-col sm:flex-row items-start sm:items-end gap-6">
					<div className="flex-1 space-y-4">
						<div>
							<label
								htmlFor="transfer-to-workspace"
								className="block text-xs uppercase tracking-wide font-bold mb-2"
								style={{ fontFamily: "inherit", color: billingTheme.muted }}
							>
								To Workspace
							</label>
							<select
								id="transfer-to-workspace"
								value={effectiveToWorkspaceId}
								onChange={(e) => setToWorkspaceId(e.target.value)}
								className="w-full px-4 py-3 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/50"
								style={{
									backgroundColor: billingTheme.surfaceInset,
									border: `1px solid ${billingTheme.border}`,
									color: billingTheme.text,
								}}
							>
								{otherWorkspaces.map((ws) => (
									<option key={ws.id} value={ws.id}>{ws.name}</option>
								))}
							</select>
						</div>
						<div>
							<label
								htmlFor="transfer-amount"
								className="block text-xs uppercase tracking-wide font-bold mb-2"
								style={{ fontFamily: "inherit", color: billingTheme.muted }}
							>
								Amount, available: <span className="text-[#6B3FF2]">${availableDollars}</span>
							</label>
							<div
								className="flex items-center overflow-hidden"
								style={{ backgroundColor: billingTheme.surfaceInset, border: `1px solid ${billingTheme.border}` }}
							>
								<span
									className="px-4 py-3 text-[#6B3FF2] font-mono text-lg border-r select-none"
									style={{ borderColor: billingTheme.border }}
								>
									$
								</span>
								<input
									id="transfer-amount"
									type="number"
									min="0.01"
									step="0.01"
									placeholder="0.00"
									value={amount}
									onChange={(e) => setAmount(e.target.value)}
									className="flex-1 bg-transparent px-4 py-3 text-lg font-mono focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/50"
									style={{ color: billingTheme.text }}
								/>
							</div>
						</div>
					</div>
					<button
						type="button"
						onClick={handleTransfer}
						disabled={isSubmitting || amountCents <= 0 || !effectiveToWorkspaceId}
						aria-busy={isSubmitting}
						className="shrink-0 flex items-center gap-2 px-6 py-3 text-sm font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/50"
						style={{ backgroundColor: billingTheme.accent, color: billingTheme.accentStrong }}
					>
						<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px" }}>
							swap_horiz
						</span>
						{isSubmitting ? "Transferring…" : "Transfer"}
					</button>
				</div>
			</div>
		</div>
	);
}

function WorkspacePicker({
	workspaces,
	activeWorkspaceId,
	onSwitch,
}: {
	workspaces: { id: string; name: string; role: string }[];
	activeWorkspaceId: string | null;
	onSwitch: (id: string) => void;
}) {
	const [open, setOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null);
	const active = workspaces.find((ws) => ws.id === activeWorkspaceId);

	// Close on outside click
	useEffect(() => {
		if (!open) return;
		function handleClick(e: MouseEvent) {
			if (ref.current && !ref.current.contains(e.target as Node)) {
				setOpen(false);
			}
		}
		document.addEventListener("mousedown", handleClick);
		return () => document.removeEventListener("mousedown", handleClick);
	}, [open]);

	if (workspaces.length <= 1) {
		return (
			<span
				className="font-bold text-sm px-3 py-1.5"
				style={{ backgroundColor: billingTheme.surfaceInset, color: billingTheme.text }}
			>
				{active?.name ?? "-"}
			</span>
		);
	}

	return (
		<div ref={ref} className="relative">
			<button
				type="button"
				aria-expanded={open}
				aria-haspopup="listbox"
				onClick={() => setOpen((v) => !v)}
				className="flex items-center gap-2 text-sm px-3 py-1.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/50"
				style={{
					backgroundColor: billingTheme.surfaceInset,
					border: `1px solid ${billingTheme.border}`,
					color: billingTheme.text,
				}}
			>
				<span className="font-bold">{active?.name ?? "Select workspace"}</span>
				<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px", color: billingTheme.muted }}>
					{open ? "expand_less" : "expand_more"}
				</span>
			</button>
			{open && (
				<div
					className="absolute left-0 top-full mt-1 z-50 min-w-[180px] overflow-hidden"
					style={{
						backgroundColor: billingTheme.surface,
						border: `1px solid ${billingTheme.border}`,
					}}
				>
					{workspaces.map((ws) => {
						const isActive = ws.id === activeWorkspaceId;
						return (
							<button
								key={ws.id}
								type="button"
								onClick={() => {
									onSwitch(ws.id);
									setOpen(false);
								}}
								className="w-full flex items-center justify-between gap-3 px-4 py-3 text-sm transition-colors hover:bg-[rgba(234,40,4,0.06)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
								style={{ color: billingTheme.text }}
							>
								<span className={isActive ? "font-bold" : ""}>{ws.name}</span>
								{isActive && (
									<span className="material-symbols-outlined text-[#6B3FF2]" aria-hidden="true" style={{ fontSize: "16px", fontVariationSettings: "'FILL' 1" }}>
										check_circle
									</span>
								)}
							</button>
						);
					})}
				</div>
			)}
		</div>
	);
}

function BillingPage() {
	const { user } = useSession();
	const { workspace, workspaces, switchWorkspace } = useDashboardContext();
	const navigate = useNavigate();
	const { checkout } = Route.useSearch();
	const customAmountId = useId();
	const billingHistoryId = useId();
	const [selected, setSelected] = useState<PresetAmount | null>(50);
	const [customAmount, setCustomAmount] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	const balanceQuery = useQuery({
		enabled: !!user && !!workspace,
		queryKey: ["billing", "balance", workspace?.id],
		queryFn: async () => {
			const res = await fetch("/api/mcp/credits", {
				headers: { Accept: "application/json" },
			});
			if (!res.ok) throw new Error("Failed to fetch balance");
			return res.json() as Promise<{ balance: number; balanceCents: number }>;
		},
	});

	const billingHistoryQuery = useQuery({
		enabled: !!user && !!workspace,
		queryKey: ["billing", "history", workspace?.id],
		queryFn: async () => {
			const res = await fetch("/api/mcp/billing-history?limit=20", {
				headers: { Accept: "application/json" },
			});
			if (!res.ok) throw new Error("Failed to fetch billing history");
			return res.json() as Promise<{ transactions: BillingTransaction[] }>;
		},
	});

	const balance = balanceQuery.data?.balance ?? 0;
	const formattedBalance = formatUSD(balance);

	const handleSwitchWorkspace = async (workspaceId: string) => {
		try {
			await switchWorkspace(workspaceId);
			await Promise.all([balanceQuery.refetch(), billingHistoryQuery.refetch()]);
		} catch {
			toast.error("Failed to switch workspace");
		}
	};

	const isCustom = selected === null;
	const selectedDollarAmount: number = isCustom
		? parseFloat(customAmount) || 0
		: (selected ?? 0);

	const total = selectedDollarAmount;

	useEffect(() => {
		if (!checkout) return;

		if (checkout === "success") {
			toast.success("Payment completed. Your balance will update shortly.");
			void balanceQuery.refetch();
			void billingHistoryQuery.refetch();
		} else if (checkout === "cancelled") {
			toast.info("Stripe checkout was cancelled.");
		}

			void navigate({
				to: "/dashboard/billing",
				replace: true,
				search: { checkout: undefined },
			});
	}, [balanceQuery, billingHistoryQuery, checkout, navigate]);

	const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSelected(null);
		setCustomAmount(e.target.value);
	};

	const handlePurchase = async () => {
		if (selectedDollarAmount <= 0) {
			toast.error("Please select or enter an amount.");
			return;
		}

		const amountCents = Math.round(selectedDollarAmount * 100);
		if (amountCents < 100) {
			toast.error("Minimum top-up is $1.00.");
			return;
		}

		setIsSubmitting(true);

		try {
			const response = await fetch("/api/stripe/checkout", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					amountCents,
					returnPath: "/dashboard/billing",
				}),
			});
			const payload = (await response.json().catch(() => null)) as {
				checkoutUrl?: string;
				error?: string;
			} | null;

			if (!response.ok || !payload?.checkoutUrl) {
				throw new Error(payload?.error || "Failed to start Stripe checkout");
			}

			window.location.href = payload.checkoutUrl;
		} catch (error) {
			toast.error(
				error instanceof Error
					? error.message
					: "Failed to start Stripe checkout",
			);
			setIsSubmitting(false);
		}
	};

	const transactions = (
		billingHistoryQuery.data?.transactions ?? []
	).filter((t) => t.transactionType === "credit");

	return (
		<DashboardLayout>
			{/* Page header */}
				<div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
					<div>
						<p
							className="text-[11px] font-bold uppercase tracking-[0.1em] mb-2"
							style={{ fontFamily: "inherit", color: "#6B3FF2" }}
						>
							Dashboard
						</p>
						<h1
							className="text-5xl font-semibold tracking-tighter mb-2"
							style={{
								fontFamily: "'Geist', -apple-system, sans-serif",
								color: billingTheme.text,
							}}
						>
							Add Funds
						</h1>
						<div className="flex items-center gap-3 flex-wrap">
							<p
								className="flex items-center gap-2 text-sm"
								style={{ color: billingTheme.muted }}
							>
								Balance for
							</p>
							<WorkspacePicker
								workspaces={workspaces}
								activeWorkspaceId={workspace?.id ?? null}
								onSwitch={handleSwitchWorkspace}
							/>
							<p
								className="flex items-center gap-2 text-sm"
								style={{ color: billingTheme.muted }}
							>
								is{" "}
								<span
									className="font-semibold text-xl"
									style={{ fontFamily: "'Geist Mono', 'JetBrains Mono', monospace", color: "#6B3FF2" }}
								>
									{formattedBalance}
								</span>
							</p>
						</div>
					</div>
					{/* Decorative separator line */}
					<div
						className="hidden lg:block h-px flex-1 mx-8 mb-4"
						style={{
							background:
								"linear-gradient(to right, rgba(234,40,4,0.3), transparent)",
						}}
					/>
				</div>

				{/* Main grid */}
				<div className="grid grid-cols-12 gap-12">
					{/* Left: 8 cols */}
					<div className="col-span-12 lg:col-span-8 space-y-10">
						{/* Quick top-up presets */}
						<div>
							<div className="flex items-center gap-4 mb-6">
								<h2
									className="text-xs uppercase tracking-[0.1em] font-bold shrink-0"
									style={{
										fontFamily: "inherit",
										color: "#6B3FF2",
									}}
								>
									Quick Top-Up
								</h2>
								<div
									className="h-px flex-1"
									style={{ backgroundColor: billingTheme.borderSoft }}
								/>
							</div>
							<div className="grid grid-cols-2 gap-4">
								{PRESETS.map((preset) => {
									const isSelected = selected === preset.amount;
									return (
										<button
											key={preset.amount}
											type="button"
											aria-pressed={isSelected}
											onClick={() => {
												setSelected(preset.amount);
												setCustomAmount("");
											}}
											className={`relative overflow-hidden p-6 cursor-pointer transition-all text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60 ${
												isSelected
													? "border-2 border-[#6B3FF2]"
													: ""
											}`}
											style={
												isSelected
													? {
															background: billingTheme.surfaceAlt,
															borderColor: billingTheme.accent,
														}
													: {
															background: billingTheme.surface,
															border: `1px solid ${billingTheme.borderSoft}`,
														}
											}
											onMouseEnter={(e) => {
												if (!isSelected) {
													e.currentTarget.style.borderColor =
														"rgba(234,40,4,0.4)";
													e.currentTarget.style.background =
														"rgba(234,40,4,0.04)";
												}
											}}
											onMouseLeave={(e) => {
												if (!isSelected) {
													e.currentTarget.style.borderColor =
														billingTheme.borderSoft;
													e.currentTarget.style.background =
														billingTheme.surface;
												}
											}}
											onFocus={(e) => {
												if (!isSelected) {
													e.currentTarget.style.borderColor =
														"rgba(234,40,4,0.4)";
													e.currentTarget.style.background =
														"rgba(234,40,4,0.04)";
												}
											}}
											onBlur={(e) => {
												if (!isSelected) {
													e.currentTarget.style.borderColor =
														billingTheme.borderSoft;
													e.currentTarget.style.background =
														billingTheme.surface;
												}
											}}
										>
											{/* Subtle gradient overlay on selected */}
											{isSelected && (
												<div
													className="absolute inset-0 pointer-events-none"
													style={{
														background:
															"linear-gradient(135deg, rgba(234,40,4,0.04), transparent)",
													}}
												/>
											)}
											{preset.label && (
												<span
													className="absolute top-3 right-3 text-[10px] font-semibold uppercase px-2 py-0.5"
													style={{
														fontFamily: "inherit",
														backgroundColor: "#6B3FF2",
														color: "#ffffff",
													}}
												>
													{preset.label}
												</span>
											)}
											{isSelected && (
												<span className="absolute top-3 left-3 flex items-center justify-center size-4 bg-[#6B3FF2]">
													<span
														className="material-symbols-outlined text-[#ffffff]"
														aria-hidden="true"
														style={{
															fontSize: "10px",
															fontVariationSettings: "'FILL' 1",
														}}
													>
														check
													</span>
												</span>
											)}
											<div className="mt-2 relative z-10">
												<p
													className="text-3xl font-semibold tracking-tight"
													style={{
														fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
														color: billingTheme.text,
													}}
												>
													${preset.amount}
												</p>
												<p
													className="mt-2 text-[10px] uppercase tracking-wide"
													style={{
														fontFamily: "inherit",
														color: billingTheme.muted,
													}}
												>
													Wallet Top-Up
												</p>
											</div>
										</button>
									);
								})}
							</div>
						</div>

						{/* Custom Amount */}
						<div>
							<div className="flex items-center gap-4 mb-6">
								<h2
									className="text-xs uppercase tracking-[0.1em] font-bold shrink-0"
									style={{
										fontFamily: "inherit",
										color: "#6B3FF2",
									}}
								>
									Custom Amount
								</h2>
								<div
									className="h-px flex-1"
									style={{ backgroundColor: billingTheme.borderSoft }}
								/>
							</div>
							<div
								className="p-6"
								style={{
									backgroundColor: billingTheme.surface,
									border: `1px solid ${billingTheme.border}`,
									borderTop: "3px solid #6B3FF2",
								}}
							>
								<div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
									<div className="w-full sm:w-1/2">
										<label
											htmlFor={customAmountId}
											className="block text-xs uppercase tracking-wide font-bold mb-3"
												style={{
													fontFamily: "inherit",
													color: billingTheme.muted,
												}}
										>
											Enter Amount
										</label>
										<div
											className={`flex items-center overflow-hidden transition-colors ${
												isCustom && customAmount
													? "border border-[#6B3FF2]"
													: ""
											}`}
											style={{
												backgroundColor: billingTheme.surfaceInset,
												borderColor:
													isCustom && customAmount
														? billingTheme.accent
														: billingTheme.border,
											}}
										>
											<span
												className="px-4 py-4 text-[#6B3FF2] font-mono text-lg border-r select-none"
												style={{ borderColor: billingTheme.border }}
											>
												$
											</span>
											<input
												id={customAmountId}
												type="number"
												min="1"
												step="0.01"
												placeholder="Enter amount..."
												value={customAmount}
												onChange={handleCustomAmountChange}
												className="flex-1 bg-transparent px-4 py-4 text-xl font-mono focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/50"
												style={{
													color: billingTheme.text,
												}}
											/>
										</div>
									</div>
									{isCustom && selectedDollarAmount > 0 && (
										<p className="text-sm font-mono text-[#6B3FF2]">
											{formatUSD(selectedDollarAmount)} will be added to your
											balance
										</p>
									)}
								</div>
							</div>
						</div>

						{/* Transfer Funds */}
						<TransferFundsSection
							activeWorkspaceId={workspace?.id ?? null}
							activeBalanceCents={balanceQuery.data?.balanceCents ?? 0}
							onTransferSuccess={() => {
								void balanceQuery.refetch();
								void billingHistoryQuery.refetch();
							}}
						/>
					</div>

					{/* Right: 4 cols, Purchase Summary */}
					<div className="col-span-12 lg:col-span-4">
						<div
							className="sticky top-24 p-8 space-y-6 overflow-hidden"
							style={{
								background: billingTheme.surfaceGlass,
								backdropFilter: "blur(20px)",
								border: `1px solid ${billingTheme.border}`,
								borderTop: "3px solid #6B3FF2",
							}}
						>
							<h2
								className="text-2xl font-semibold tracking-tighter"
								style={{
									fontFamily: "'Geist', -apple-system, sans-serif",
									color: billingTheme.text,
								}}
							>
								Purchase Summary
							</h2>

							<div className="space-y-5 text-sm">
								<div className="flex items-center justify-between">
										<span
											style={{
												color: billingTheme.muted,
												fontFamily: "inherit",
											}}
									>
										Selected Amount
									</span>
									<span
										className="font-mono font-bold"
										style={{ color: billingTheme.text }}
									>
										{selectedDollarAmount > 0
											? formatUSD(selectedDollarAmount)
											: "-"}
									</span>
								</div>
								<div className="flex items-center justify-between">
										<span
											style={{
												color: billingTheme.muted,
												fontFamily: "inherit",
											}}
									>
										Funds Added
									</span>
									<span className="font-mono font-bold" style={{ color: "#2b9a66" }}>
										{selectedDollarAmount > 0
											? `+${formatUSD(selectedDollarAmount)}`
											: "-"}
									</span>
								</div>
								<div
									className="pt-4 flex items-end justify-between"
									style={{ borderTop: `1px solid ${billingTheme.borderSoft}` }}
								>
									<div>
										<p
											className="text-[10px] uppercase tracking-wide font-bold mb-1"
												style={{
													fontFamily: "inherit",
													color: billingTheme.muted,
												}}
										>
											Total Charge
										</p>
										<span
											className="text-3xl font-semibold"
											style={{
												fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
												color: billingTheme.text,
											}}
										>
											{selectedDollarAmount > 0 ? formatUSD(total) : "-"}
										</span>
									</div>
										<span
											className="text-[10px] pb-1"
											style={{ color: billingTheme.muted }}
										>
										USD
									</span>
								</div>
							</div>

							<button
								type="button"
								onClick={handlePurchase}
								disabled={selectedDollarAmount <= 0 || isSubmitting}
								aria-busy={isSubmitting}
								className="w-full flex items-center justify-center gap-2 bg-[#6B3FF2] text-[#ffffff] font-semibold py-5 text-sm transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/50"
								style={{
									backgroundColor: billingTheme.accent,
									color: billingTheme.accentStrong,
								}}
							>
								<span
									style={{
										fontFamily: "inherit",
										letterSpacing: "0.1em",
									}}
								>
									{isSubmitting
										? "REDIRECTING TO STRIPE"
										: "CONTINUE TO STRIPE"}
								</span>
								<span
									className="material-symbols-outlined group-hover:translate-x-1 transition-transform"
									aria-hidden="true"
									style={{ fontSize: "16px" }}
								>
									arrow_forward
								</span>
							</button>

							<div
								className="flex items-start gap-3 p-4"
								style={{ backgroundColor: billingTheme.rowFill, borderLeft: "3px solid #2b9a66" }}
							>
								<span
									className="material-symbols-outlined shrink-0 mt-0.5"
									aria-hidden="true"
									style={{ fontSize: "14px", color: billingTheme.muted }}
								>
									verified_user
								</span>
								<p
									className="text-[10px] leading-relaxed"
									style={{
										fontFamily: "inherit",
										color: billingTheme.muted,
									}}
								>
									Secured by 256-bit encryption. Funds are added to your wallet
									balance as soon as payment is confirmed.
								</p>
							</div>
						</div>
					</div>
				</div>

				<div className="mt-20" id={billingHistoryId}>
					<div className="flex items-center gap-4 mb-6">
						<h2
							className="text-xs uppercase tracking-[0.1em] font-bold shrink-0"
								style={{
									fontFamily: "inherit",
									color: "#6B3FF2",
								}}
						>
							Billing History
						</h2>
						<div
							className="h-px flex-1"
							style={{ backgroundColor: billingTheme.borderSoft }}
						/>
					</div>

					<div
						className="overflow-hidden"
						style={{
							backgroundColor: billingTheme.surface,
							border: `1px solid ${billingTheme.border}`,
							borderTop: "3px solid #6B3FF2",
						}}
					>
						{billingHistoryQuery.isLoading ? (
							<div
								className="p-8 text-sm"
								style={{ color: billingTheme.muted }}
							>
								Loading transactions...
							</div>
						) : transactions.length === 0 ? (
							<div
								className="p-8 text-sm"
								style={{ color: billingTheme.muted }}
							>
								No billing activity yet. Your top-ups and usage charges will
								appear here.
							</div>
						) : (
							<div
							role="list"
							aria-label="Billing transactions"
							className="divide-y"
							style={{ borderColor: billingTheme.borderSoft }}
						>
							{transactions.map((transaction) => {
								const isCredit = transaction.amountCents > 0;
								return (
									<div
										key={transaction.id}
										role="listitem"
										className="grid grid-cols-1 md:grid-cols-[1.4fr_0.9fr_0.7fr] gap-4 px-5 py-4"
									>
											<div className="min-w-0">
												<p
													className="text-sm font-bold"
													style={{ color: billingTheme.text }}
												>
													{getTransactionLabel(transaction)}
												</p>
												<p
													className="text-xs mt-1"
													style={{ color: billingTheme.muted }}
												>
													{new Date(transaction.createdAt).toLocaleString(
														"en-US",
														{
															month: "short",
															day: "numeric",
															year: "numeric",
															hour: "numeric",
															minute: "2-digit",
														},
													)}
												</p>
											</div>
											<div className="flex items-center">
												<span
													className="text-[10px] uppercase tracking-[0.1em] font-bold px-2 py-1"
													style={{
														fontFamily: "inherit",
														backgroundColor: isCredit
															? "rgba(43,154,102,0.12)"
															: "rgba(234,40,4,0.08)",
														border: isCredit
															? "1px solid #2b9a66"
															: "1px solid #6B3FF2",
														borderLeft: isCredit
															? "2px solid #2b9a66"
															: "2px solid #6B3FF2",
														color: isCredit
															? "#2b9a66"
															: "#6B3FF2",
													}}
												>
													{getProviderLabel(transaction.provider)} •{" "}
													{transaction.transactionType}
												</span>
											</div>
											<div className="flex items-center md:justify-end">
												<span
													className="font-mono text-sm font-bold"
													style={{
														color: isCredit
															? "#2b9a66"
															: billingTheme.text,
													}}
												>
													{isCredit ? "+" : "-"}
													{formatMoneyFromCents(
														Math.abs(transaction.amountCents),
													)}
												</span>
											</div>
										</div>
									);
								})}
							</div>
						)}
					</div>
				</div>

		</DashboardLayout>
	);
}
